import React, { useState, useMemo } from 'react';
import { Stack, CircularProgress, Button, Typography, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { ActionMenu } from 'components/_atoms';
import Modal from 'components/_layouts/Modal';
import { translate as t } from 'hooks/useTranslations';
import { enqueueSnackbar } from 'notistack';
import { useQueryClient } from 'react-query';
import parse from 'html-react-parser';
import errorHelper from 'helpers/errorHelper';
import { republishStandard } from '../../../../_data_sources/AccreditationStandards/manageStandard';
import { publishAccreditationVersion } from '../../../../_data_sources/AccreditationVersions/manageAccreditationVersions';
import { ServerErrorResponse } from 'data_sources/constants';
import useAccreditationVersion from 'views/App/Portal/AWB/_data_sources/AccreditationVersions/useAccreditationVersion';
import { JurisdictionWithPublishedState } from '../../../../_types/awb';

const StandardActions: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: accreditationId, versionId, standardId } = useParams();
  const versionQuery = useAccreditationVersion(accreditationId as string, versionId as string);
  const [isNavigating, setIsNavigating] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalAction, setModalAction] = useState<'standard' | 'version' | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [notificationMode, setNotificationMode] = useState<'silent' | 'notify'>('silent');

  const hasPublishedJurisdiction = useMemo(() => {
    if (!versionQuery.data?.jurisdictions) return false;

    const jurisdictions = versionQuery.data.jurisdictions as JurisdictionWithPublishedState[];
    return jurisdictions.some((jurisdiction) => jurisdiction.publishedState === 'PUBLISHED');
  }, [versionQuery.data?.jurisdictions]);

  const handleManageStructureClick = () => {
    setIsNavigating(true);
    navigate(`/admin/awb/accrediting-bodies/${accreditationId}/versions/${versionId}/standards/manage-structure`);
  };

  const handleReleaseClick = () => {
    setModalAction('standard');
    setShowConfirmModal(true);
  };

  const handleRepublishVersionClick = () => {
    if (!versionQuery.data?.jurisdictions) return;

    const jurisdictions = versionQuery.data.jurisdictions as JurisdictionWithPublishedState[];

    if (jurisdictions.length > 1) {
      navigate(`/admin/awb/accrediting-bodies/${accreditationId}/versions/${versionId}/release`);
      return;
    }

    const singleJurisdiction = jurisdictions[0];

    if (singleJurisdiction.publishedState !== 'DRAFT') {
      setModalAction('version');
      setShowConfirmModal(true);
    } else {
      performVersionRelease([singleJurisdiction.id]);
    }
  };

  const performVersionRelease = (jurisdictionIds: string[]) => {
    if (!accreditationId || !versionId) return;

    setIsSaving(true);

    const publishData = {
      jurisdictions: jurisdictionIds,
      republishNotificationRequired: notificationMode === 'notify',
    };

    publishAccreditationVersion(
      accreditationId,
      versionId,
      publishData,
      () => {
        setIsSaving(false);
        setShowConfirmModal(false);
        setModalAction(null);

        enqueueSnackbar(
          parse(
            t('awb.accreditingBodyVersions.release.successToast', '', {
              versionName: versionQuery.data?.version || '',
              jurisdictionCount: jurisdictionIds.length.toString(),
            })
          ),
          {
            variant: 'success',
            autoHideDuration: 6000,
          }
        );

        queryClient.invalidateQueries(['accreditation_versions', accreditationId]);
        queryClient.invalidateQueries(['accreditation_version', accreditationId, versionId]);
      },
      (errors: ServerErrorResponse) => {
        setIsSaving(false);
        setShowConfirmModal(false);
        setModalAction(null);
        console.error('Version release error:', errors);
        errorHelper.enqueueErrors(errors);
      }
    );
  };

  const handleConfirmAction = () => {
    if (modalAction === 'version') {
      if (!versionQuery.data?.jurisdictions) return;

      const jurisdictions = versionQuery.data.jurisdictions as JurisdictionWithPublishedState[];
      const jurisdictionIds = jurisdictions.map((j) => j.id);

      performVersionRelease(jurisdictionIds);
    } else if (modalAction === 'standard') {
      handleConfirmRepublish();
    }
  };

  const handleConfirmRepublish = () => {
    if (!accreditationId || !versionId || !standardId) return;

    setIsSaving(true);

    const republishData = {
      republishNotificationRequired: notificationMode === 'notify',
    };

    republishStandard(
      accreditationId,
      versionId,
      standardId,
      republishData,
      () => {
        setIsSaving(false);
        setShowConfirmModal(false);
        setModalAction(null);

        enqueueSnackbar(parse(t('awb.standards.republish.successToast')), {
          variant: 'success',
          autoHideDuration: 6000,
        });

        queryClient.invalidateQueries(['accreditation_standards', accreditationId, versionId]);
        queryClient.invalidateQueries(['accreditation_standard', accreditationId, versionId, standardId]);
      },
      (errors: ServerErrorResponse) => {
        setIsSaving(false);
        setShowConfirmModal(false);
        setModalAction(null);
        console.error('Republish error:', errors);
        errorHelper.enqueueErrors(errors);
      }
    );
  };

  return (
    <Stack
      direction='row'
      spacing={2}
    >
      <ActionMenu
        id='version-menu'
        btnText='Version'
        btnProps={{
          variant: 'ghost',
          color: 'neutral',
          size: 'sm',
        }}
        items={[
          {
            key: 'manage-structure',
            label: t('common.manageStructure'),
            onClick: handleManageStructureClick,
            disabled: isNavigating,
          },
        ]}
      />
      <ActionMenu
        id='view-menu'
        btnText='View'
        btnProps={{
          variant: 'ghost',
          color: 'neutral',
          size: 'sm',
        }}
        items={[]}
      />
      <ActionMenu
        id='view-menu'
        btnText='Release'
        btnProps={{
          variant: 'ghost',
          color: 'neutral',
          size: 'sm',
        }}
        items={[
          {
            key: 'republish-version',
            label: t('common.republishVersion'),
            onClick: handleRepublishVersionClick,
            disabled: isSaving || versionQuery.isLoading || !hasPublishedJurisdiction,
          },
          {
            key: 'release',
            label: t('common.republishCurrentStandard'),
            onClick: handleReleaseClick,
            disabled: isSaving || !standardId || versionQuery.isLoading || !hasPublishedJurisdiction,
          },
        ]}
      />
      {isNavigating && <CircularProgress size={20} />}

      <Modal
        open={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setModalAction(null);
        }}
        title={t('awb.accreditingBodyVersions.release.modal.title')}
        subtitle={modalAction === 'version' ? t('common.republishVersion') : t('common.republishCurrentStandard')}
        size='sm'
        actions={
          <>
            <Button
              variant='outlined'
              color='neutral'
              onClick={() => {
                setShowConfirmModal(false);
                setModalAction(null);
              }}
              disabled={isSaving}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant='contained'
              onClick={handleConfirmAction}
              disabled={isSaving}
              endIcon={isSaving ? <CircularProgress size={16} /> : null}
            >
              {modalAction === 'version'
                ? t('awb.accreditingBodyVersions.release.modal.confirm')
                : t('awb.standards.republish.modal.confirm')}
            </Button>
          </>
        }
      >
        <Stack spacing={3}>
          <Typography variant='body1'>
            {modalAction === 'version'
              ? t('awb.accreditingBodyVersions.release.modal.description')
              : t('awb.standards.republish.modal.description')}
          </Typography>

          <RadioGroup
            value={notificationMode}
            onChange={(e) => setNotificationMode(e.target.value as 'silent' | 'notify')}
          >
            <FormControlLabel
              value='silent'
              control={<Radio />}
              label={
                <Stack>
                  <Typography
                    variant='body1'
                    fontWeight={600}
                  >
                    {t('awb.accreditingBodyVersions.release.modal.silent.title')}
                  </Typography>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                  >
                    {t('awb.accreditingBodyVersions.release.modal.silent.description')}
                  </Typography>
                </Stack>
              }
            />
            <FormControlLabel
              value='notify'
              control={<Radio />}
              label={
                <Stack>
                  <Typography
                    variant='body1'
                    fontWeight={600}
                  >
                    {t('awb.accreditingBodyVersions.release.modal.notify.title')}
                  </Typography>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                  >
                    {t('awb.accreditingBodyVersions.release.modal.notify.description')}
                  </Typography>
                </Stack>
              }
            />
          </RadioGroup>
        </Stack>
      </Modal>
    </Stack>
  );
};

export default StandardActions;
